import { Toast } from "@base-ui/react/toast";
import { "ui-overlay.notifications" as notificationsLabel } from "@repo/i18n";
import { Check } from "lucide-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../controls/icon";

/** A2's timings (store.tsx `notify`): an offer to undo stays long enough to reach for it. */
export const TOAST_TIMEOUT_MS = { plain: 2600, withAction: 5000 } as const;

export interface ToastAction {
  label: string;
  /** Called at most once, on a press. Never on auto-dismiss or a swipe. */
  onAction: () => void;
}

/**
 * `success` carries the green check — "Série registrada". `info` carries no
 * badge at all: "Nova versão disponível" and a foreground push are neither a
 * success nor a failure, and a check on them is a lie the eye reads first.
 */
export type ToastTone = "success" | "info";

export interface ToastInput {
  action?: ToastAction;
  /**
   * A second, quieter line under the message: what the edit did beyond
   * itself ("Peito: 9 → 6 séries/semana").
   */
  detail?: ReactNode;
  message: string;
  /**
   * How long it stays, in ms. Default {@link TOAST_TIMEOUT_MS}: 5 s with an
   * action, 2.6 s without. A story that taps the action through Node's real
   * touch passes a long one, so a loaded machine cannot let the toast expire
   * between finding the button and the tap landing.
   */
  timeout?: number;
  /** Default `"success"`. */
  tone?: ToastTone;
}

/** Through a function: Biome narrows `ref.current` to its initial `false`. */
function hasFired(flag: { current: boolean }): boolean {
  return flag.current;
}

interface ToastData {
  action?: ToastAction;
  detail?: ReactNode;
  tone?: ToastTone;
}

const DESKTOP = "(min-width: 1024px)";

/**
 * Every mounted bottom zone, by the token its hook instance owns. A Map, not
 * a single number: the tab bar, the sign-in actions and the workout's bar are
 * separate components, and the order in which one unmounts and the next
 * mounts must not decide what the variable holds.
 */
const zones = new Map<object, number>();

/** `--action-bar` is the MAX of the registry, and absent when it is empty. */
function publishClearance(): void {
  const html = document.documentElement;
  if (zones.size === 0) {
    html.style.removeProperty("--action-bar");
    return;
  }
  let clearance = 0;
  for (const value of zones.values()) {
    clearance = Math.max(clearance, value);
  }
  html.style.setProperty("--action-bar", `${clearance}px`);
}

/**
 * Keeps the toast clear of a bottom action zone: the tab bar's centre button,
 * sign-in's pinned actions, the workout's "Registrar série" (D15, T54) — the
 * toast lands on whichever of them is on screen otherwise, and A2's top toast
 * covered "Encerrar", so moving it up is not an option.
 *
 * The registered value is the element's CLEARANCE — the distance from its TOP
 * edge to the viewport bottom — not its height: a zone that floats above the
 * edge (the tab bar's button sticks 28 px out of the bar) needs the gap, and
 * a zone that pads itself clear of the gesture area has already counted the
 * safe-area inset inside itself. That is why the viewport takes the max of
 * the inset and this value rather than their sum.
 *
 * Measured on mount, on `ResizeObserver`, and on window `resize` — the
 * keyboard resizes the viewport (`interactive-widget=resizes-content`) and
 * moves every pinned zone with it. On a desktop (≥1024 px) the action zone is
 * a side column, so the value is 0.
 *
 * Written on `<html>`: the toast viewport is portalled into `<body>` and
 * inherits nothing from the screen that published the zone.
 */
export function useToastClearance(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const token = {};
    const measure = () => {
      const desktop = window.matchMedia?.(DESKTOP).matches ?? false;
      const { top } = element.getBoundingClientRect();
      // A column taller than the viewport puts the zone below the fold; the
      // clamp keeps that from reading as a negative offset.
      const clearance = desktop
        ? 0
        : Math.max(0, Math.round(window.innerHeight - top));
      zones.set(token, clearance);
      publishClearance();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      zones.delete(token);
      publishClearance();
    };
  }, [ref]);
}

/**
 * The single toast channel. Mount once near the root; `useToast()` below it.
 *
 * One toast at a time: a new one closes the old, as A2's did. The viewport
 * sits at the BOTTOM, above whichever action zone is on screen — every one of
 * them registers itself through `useToastClearance` as `--action-bar` (D15,
 * T54) — because A2's top toast covered "Encerrar".
 *
 * The offset is the MAX of the safe-area inset and that clearance, never
 * their sum: a zone measured from its top edge already contains whatever
 * padding it gave itself for the gesture area, and adding the inset again
 * lifted the toast a gesture bar clear of the workout's own bar.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider limit={1}>
      {children}
      <Toast.Portal>
        <Toast.Viewport
          aria-label={notificationsLabel()}
          className="fixed inset-x-0 bottom-[calc(max(env(safe-area-inset-bottom)+var(--safe-area-inset-bottom,0px),var(--action-bar,0px))+8px)] z-[60] mx-auto w-[calc(100%-40px)] max-w-[440px] lg:right-[var(--coach-dock,0px)]"
          data-toast-viewport=""
        >
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}

function ToastList() {
  const { toasts } = Toast.useToastManager<ToastData>();
  return toasts.map((toast) => <ToastItem key={toast.id} toast={toast} />);
}

function ToastItem({ toast }: { toast: Toast.Root.ToastObject<ToastData> }) {
  const { close } = Toast.useToastManager();
  // A double tap, or a tap during the exit transition, must not run the
  // action twice: A2's "Desfazer" restores a snapshot, and twice is wrong.
  const fired = useRef<boolean>(false);
  const action = toast.data?.action;
  const detail = toast.data?.detail;
  const success = (toast.data?.tone ?? "success") === "success";
  const { id } = toast;

  const runAction = useCallback(() => {
    if (hasFired(fired) || !action) {
      return;
    }
    fired.current = true;
    action.onAction();
    close(id);
  }, [action, close, id]);

  return (
    <Toast.Root
      aria-live="polite"
      className="absolute inset-x-0 bottom-0 flex justify-center transition-[opacity,transform] duration-200 data-ending-style:translate-y-3 data-starting-style:translate-y-3 data-ending-style:opacity-0 data-limited:opacity-0 data-starting-style:opacity-0"
      role="status"
      toast={toast}
    >
      <Toast.Content
        className={cx(
          "flex min-h-12 items-center gap-2.5 rounded-2xl bg-inverse py-2.5 pr-5 font-semibold text-base text-on-inverse leading-snug shadow-lift",
          // Without the badge the text would otherwise start 12 px in.
          success ? "pl-3" : "pl-5"
        )}
      >
        {success ? (
          <span
            className="grid size-6 shrink-0 place-items-center rounded-full bg-success text-on-accent"
            data-toast-badge
          >
            {/* 14 px at stroke 3 before T76; 16 px bold is the nearest the
                wrapper allows, and one icon does not earn a new `IconSize`. */}
            <Icon bold icon={Check} size={16} />
          </span>
        ) : null}
        <div className="min-w-0">
          <Toast.Description className="text-balance" />
          {/* The inverse fill has no secondary ink of its own — `ink-2` on it
              is ink on ink — so the detail is its text token, dimmed. */}
          {detail ? (
            <div
              className="mt-0.5 text-balance font-medium text-on-inverse/75 text-sm"
              data-toast-detail
            >
              {detail}
            </div>
          ) : null}
        </div>
        {action ? (
          <Toast.Action
            className="a-press -my-1 -mr-3 ml-1 h-12 min-w-12 shrink-0 rounded-full bg-canvas px-4 font-bold text-ink text-sm"
            onClick={runAction}
          >
            {action.label}
          </Toast.Action>
        ) : null}
      </Toast.Content>
    </Toast.Root>
  );
}

/**
 * `notify({ message, detail?, action?, tone? })`. Must be called below a
 * `ToastProvider`.
 */
export function useToast() {
  const { add, close } = Toast.useToastManager<ToastData>();

  const notify = useCallback(
    ({ message, detail, action, timeout, tone }: ToastInput): string => {
      close();
      return add({
        description: message,
        timeout:
          timeout ??
          (action ? TOAST_TIMEOUT_MS.withAction : TOAST_TIMEOUT_MS.plain),
        data: { action, detail, tone },
      });
    },
    [add, close]
  );

  const dismiss = useCallback(() => close(), [close]);

  return { notify, dismiss };
}
